import { combineReducers, AnyAction } from "redux";
import { PagedResponseEmpty } from "@ofqual/portal-core/core/types";
import { RecipientOrg } from "../domain/RecipientsViewModel";
import {
  LOADED_ACTIONS,
  LOADED_ITEM,
  LOADED_ITEMS,
  LOADED_RECIPIENTS,
  LOADING_ACTIONS,
  LOADING_ITEM,
  LOADING_ITEMS,
  LOADING_RECIPIENTS,
  SAVED_ITEM,
  SENDING_ITEM,
  SENT_ITEM,
  TOGGLE_SELECT_RECIPIENT,
  UPDATING_ACTION,
  UPDATING_ITEM,
  UPDATING_RECIPIENTS,
} from "./actions";
import { ActionsState, ItemState, ItemsState, RecipientsState } from "./state";
import { noCrudResult, successCrudResult } from "./types";

const itemInitialState: ItemState = {
  status: "NOT_LOADED",
  item: null,
  lastCrudResult: noCrudResult(),
};

const itemsInitialState: ItemsState = {
  status: "NOT_LOADED",
  response: PagedResponseEmpty,
  hasData: false,
};

const items = (
  state: ItemsState = itemsInitialState,
  action: AnyAction
): ItemsState => {
  const { type, payload } = action;
  switch (type) {
    case LOADING_ITEMS: 
      return {
        ...state,
        status: "LOADING",
      };
    case LOADED_ITEMS:
      return {
        ...state,
        status: "LOADED",
        response: payload,
        hasData: true,
      };
    default:
      return state;
  }
}

const item = (
  state: ItemState = itemInitialState,
  action: AnyAction
): ItemState => {
  const { type, payload } = action;
  switch (type) {
    case LOADING_ITEM:
      return {
        ...state,
        item: null,
        status: "LOADING",
        lastCrudResult: noCrudResult(),
      };
    case UPDATING_ITEM:
      return {
        ...state,
        status: "UPDATING",
        lastCrudResult: noCrudResult(),
      };
    case LOADED_ITEM:
      return {
        ...state,
        status: "LOADED",
        item: payload,
      };
    case SAVED_ITEM:
      return {
        ...state,
        status: "LOADED",
        item: payload,
        lastCrudResult: successCrudResult(),
      };
    case SENDING_ITEM:
      return {
        ...state,
        status: "UPDATING",
        lastCrudResult: noCrudResult(),
      };
    case SENT_ITEM:
      return {
        ...state,
        status: "LOADED",
        item: payload,
        lastCrudResult: successCrudResult(),
      };
    default:
      return state;
  }
};

const recipientsInitialState: RecipientsState = {
  status: "NOT_LOADED",
  recipients: null,
  viewModel: null,
  lastCrudResult: noCrudResult(),
  resourceId: null,
};

const recipients = (
  state: RecipientsState = recipientsInitialState,
  action: AnyAction
): RecipientsState => {
  const { type, payload } = action;
  switch (type) {
    case LOADING_RECIPIENTS:
      return {
        ...state,
        resourceId: null,
        recipients: null,
        status: "LOADING",
        lastCrudResult: noCrudResult(),
      };

    case UPDATING_RECIPIENTS:
      return {
        ...state,
        status: "UPDATING",
        lastCrudResult: noCrudResult(),
      };
    case LOADED_RECIPIENTS:
      return {
        ...state,
        status: "LOADED",
        recipients: payload.recipients,
        resourceId: payload.resourceId,
        viewModel: payload.viewModel,
      };
    case TOGGLE_SELECT_RECIPIENT:
      const { recipients } = payload as {
        recipients: RecipientOrg[];
      };
      const newViewModel = { ...state.viewModel! };

      for (const recipient of recipients) {
        if (newViewModel.orgs) {
          const foundIndex = newViewModel.orgs.findIndex(
            (x) => x.id === recipient.id
          );
          if (foundIndex !== -1) {
            newViewModel.orgs[foundIndex] = {
              ...newViewModel.orgs[foundIndex],
              isSelected: !recipient.isSelected,
            };
          }
        }
      }
      return {
        ...state,
        viewModel: newViewModel,
      };
    default:
      return state;
  }
};

const actionsInitialState: ActionsState = {
  status: "NOT_LOADED",
  actions: null,
  resourceId: null,
};

const actions = (
  state: ActionsState = actionsInitialState,
  action: AnyAction
): ActionsState => {
  const { type, payload } = action;
  switch (type) {
    case LOADING_ACTIONS:
    case UPDATING_ACTION:
      return {
        ...state,
        actions: null,
        status: "LOADING",
        resourceId: null,
      };
    case LOADED_ACTIONS:
      return {
        ...state,
        status: "LOADED",
        actions: payload.actions,
        resourceId: payload.resourceId,
      };
    default:
      return state;
  }
};

export const rootReducer = combineReducers({
  items,
  item,
  recipients,
  actions,
});
